import styled from "@emotion/styled";
import { Box, Link } from "@mui/material";
import {
  DataGrid,
  GridColumns,
  GridToolbar,
  GridActionsCellItem,
  GridRowParams,
} from "@mui/x-data-grid";
import { deleteCryptosModified, getCryptosModified } from "src/Api";
import { CRYPTOS_MODIFIED } from "src/Api/endpoints";
import { CryptoIcon } from "src/Common";
import { ExchangeIcon } from "src/Common/components/ExchangeIcon";
import { Crypto } from "src/Modules/Arbitrage/types";
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ModifiedCrypto } from "../types";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { toast } from "react-toastify";

type ParamsColumnTarget = { row: { cryptoTarget: Crypto } };

export const ExchangeMapperTable = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery(
    CRYPTOS_MODIFIED,
    getCryptosModified
  );

  const { mutateAsync: deleteAsync } = useMutation(
    (id: ModifiedCrypto["id"]) => {
      return deleteCryptosModified(id);
    }
  );

  const handleDelete = useCallback(
    async (crypto: ModifiedCrypto) => {
      deleteAsync(crypto.id)
        .then(() =>
          toast(`${crypto.symbol} mapping successfully deleted!`, {
            theme: "dark",
            type: "success",
            autoClose: 8000,
            pauseOnFocusLoss: false,
          })
        )
        .finally(() => {
          queryClient.invalidateQueries(CRYPTOS_MODIFIED);
        });
    },
    [deleteAsync, queryClient]
  );

  const columns = useMemo<GridColumns>(
    () => [
      {
        headerName: "Symbol",
        field: "symbol",
        type: "string",
        flex: 1,
        minWidth: 100,
      },
      {
        headerName: "Exchange",
        field: "exchange",
        type: "string",
        flex: 1,
        minWidth: 100,
        renderCell: ({ value }) => (
          <Box display="flex" alignItems="center">
            <ExchangeIcon name={value} style={{ height: "1.2rem" }} />
            <Label>{value}</Label>
          </Box>
        ),
      },

      {
        headerName: "[ TGT ] Symbol",
        field: "cryptoTarget.symbol",
        valueGetter: ({ row: { cryptoTarget } }: ParamsColumnTarget) =>
          cryptoTarget.symbol,
        type: "string",
        renderCell: ({ row: { cryptoTarget } }: ParamsColumnTarget) => (
          <Box display="flex" alignItems="center">
            <CryptoIcon slug={cryptoTarget.slug} style={{ height: "1.2rem" }} />
            <Label>{cryptoTarget.symbol}</Label>
          </Box>
        ),
        flex: 1,
        minWidth: 100,
      },
      {
        headerName: "[ TGT ] name",
        field: "cryptoTarget.name",
        valueGetter: ({ row: { cryptoTarget } }: ParamsColumnTarget) =>
          cryptoTarget.name,
        type: "string",
        flex: 1,
        minWidth: 100,
      },
      {
        headerName: "[ TGT ] Active",
        field: "cryptoTarget.isActive",
        valueGetter: ({ row: { cryptoTarget } }: ParamsColumnTarget) =>
          cryptoTarget,
        type: "boolean",
        flex: 1,
        minWidth: 100,
      },
      {
        headerName: "[ TGT ] Id",
        field: "cryptoTarget",
        valueGetter: ({ value }: { value: Crypto }) => value.id,
        type: "string",
        flex: 1,
        minWidth: 100,
      },
      {
        headerName: "CoinMarketCap",
        field: "cryptoTarget.slug",
        valueGetter: ({ row: { cryptoTarget } }: ParamsColumnTarget) =>
          cryptoTarget?.slug || "",
        renderCell: ({ row: { cryptoTarget } }: ParamsColumnTarget) =>
          cryptoTarget?.slug && !cryptoTarget?.isAdded ? (
            <Link
              href={`https://coinmarketcap.com/currencies/${cryptoTarget.slug}/markets`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {cryptoTarget.slug}
            </Link>
          ) : (
            <span style={{ opacity: 0.4 }}>-</span>
          ),
        type: "string",
        flex: 1,
        minWidth: 100,
      },
      {
        headerName: "Actions",
        field: "actions",
        type: "actions",
        flex: 1,
        minWidth: 90,
        getActions: (params: GridRowParams) => {
          const celItemProps = {
            icon: <DeleteIcon />,
            label: "Delete",
            onClick: () => handleDelete(params.row),
            showInMenu: true,
            placeholder: "Optional placeholder text",
            onPointerEnterCapture: () => {},
            onPointerLeaveCapture: () => {},
            onResize: () => {},
            onResizeCapture: () => {},
          };
          return [<GridActionsCellItem {...celItemProps} />];
        },
      },
    ],
    [handleDelete]
  );

  return (
    <Box height="65vh">
      <DataGrid<ModifiedCrypto>
        editMode="row"
        error={error}
        loading={isLoading}
        style={{ border: "none" }}
        headerHeight={36}
        density="standard"
        getRowId={(r) => r.id}
        rows={data ?? []}
        columns={columns}
        rowsPerPageOptions={[50, 100]}
        components={{ Toolbar: GridToolbar }}
        componentsProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
      />
    </Box>
  );
};

const Label = styled.span({
  fontWeight: 700,
  paddingLeft: "0.5rem",
  fontSize: "0.7rem",
});
